// Ultimate Card Game (UCG) - Account creation / recovery overlay.
//
// Two jobs, both around js/net/playerIdentity.js:
// 1. showRecoveryCode() - shown exactly once, right after a brand-new
//    anonymous account is created, since the server never sends the
//    plaintext recovery code again (docs/GO_LIVE_PLAN_2026-09.md section 2.2).
//    Deliberately acknowledgment-gated (dim backdrop, must click through) -
//    unlike js/ui/onboardingGuide.js's non-blocking bubbles, this is a
//    one-shot secret that can never be shown again if missed.
// 2. The same overlay offers a "Recover Instead" path so a returning
//    player on a fresh device isn't stuck with the brand-new account this
//    session silently created for them - that account has no progress
//    attached yet, so replacing it here is lossless.

import { recoverAccount } from '../net/playerIdentity.js';

function buildRecoveryLink(recoveryCode) {
  return `${location.origin}${location.pathname}#a=${encodeURIComponent(recoveryCode)}`;
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function showRecoveryCode({ recoveryCode }) {
  const layer = el('div', 'fullscreen-dialog-overlay active account-overlay');
  layer.style.zIndex = '12000';

  layer.innerHTML = `
    <div class="fullscreen-dialog-bg-layer" aria-hidden="true">
      <div class="fullscreen-dialog-bg-image"></div>
      <div class="fullscreen-dialog-scrim"></div>
      <div class="fullscreen-dialog-vignette"></div>
    </div>
    <div class="fullscreen-dialog-container" style="justify-content: center; align-items: center; min-height: 100vh;">
      <div class="fullscreen-card-panel" style="max-width: 500px; width: 92%; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 16px; padding: clamp(28px, 4vh, 40px) clamp(22px, 3.5vw, 36px);">
        <div class="fullscreen-dialog-status-pill status-pill-circular status-gold" style="margin-bottom: -2px;">
          <span class="status-indicator-dot" style="background: #fbbf24; box-shadow: 0 0 8px #fbbf24;"></span>
        </div>
        <div class="account-overlay-title" style="font-family: 'Orbitron', sans-serif; font-size: clamp(20px, 2.5vw, 24px); font-weight: 900; letter-spacing: 2px; color: #f8fafc; text-shadow: 0 0 16px rgba(0, 240, 255, 0.4); margin: 0;">
          ACCOUNT CREATED
        </div>
        <div class="account-overlay-text" style="font-family: 'Rajdhani', sans-serif; font-size: clamp(15px, 1.8vw, 17px); font-weight: 600; line-height: 1.55; color: #cbd5e1; margin: 0;">
          This recovery code is the only way to restore your progress and rank on another device. Write it down or save the link below.
          <br>
          <span class="account-warning-highlight" style="display: inline-block; margin-top: 6px;">WILL NOT BE SHOWN AGAIN</span>
        </div>
        <div class="account-overlay-code" style="width: 100%; height: 50px; display: flex; align-items: center; justify-content: center; font-family: 'IBM Plex Mono', monospace; font-size: clamp(18px, 2.2vw, 22px); font-weight: 800; letter-spacing: 3px; color: #00f0ff; background: rgba(0, 240, 255, 0.08); border: 1.5px solid #00f0ff; border-radius: 8px; box-shadow: 0 0 20px rgba(0, 240, 255, 0.35), inset 0 0 10px rgba(0, 240, 255, 0.15); box-sizing: border-box; user-select: all; margin: 2px 0;">
          ${recoveryCode}
        </div>
        <button type="button" class="start-btn start-btn-secondary account-overlay-btn-copy" id="account-overlay-btn-copy" style="width: 100%; height: 50px;">
          <span class="start-btn-text">COPY ACCOUNT LINK</span>
        </button>
        <div class="account-link-security-note" style="display: flex; align-items: center; justify-content: center; gap: 7px; margin-top: -6px; margin-bottom: 2px; font-family: 'Rajdhani', sans-serif; font-size: clamp(12.5px, 1.5vw, 13.5px); font-weight: 700; letter-spacing: 0.8px; color: #fbbf24; text-align: center; text-transform: uppercase;">
          <span style="font-size: 13.5px; filter: drop-shadow(0 0 6px #fbbf24);">⚠</span>
          <span>Private Key — Do not share. Grants full account access.</span>
        </div>
        <label class="account-ack-row" style="display: flex; align-items: center; gap: 12px; cursor: pointer; text-align: left; width: 100%; margin: 6px 0; user-select: none;">
          <input type="checkbox" id="account-ack-checkbox" style="width: 20px; height: 20px; min-width: 20px; accent-color: #fbbf24; cursor: pointer;" />
          <span style="font-family: 'Rajdhani', sans-serif; font-size: clamp(15px, 1.8vw, 17px); font-weight: 700; color: #fbbf24; line-height: 1.35; letter-spacing: 0.3px;">
            I understand that this code / link IS THE ONLY WAY to access my account
          </span>
        </label>
        <button type="button" class="start-btn start-btn-primary" id="account-overlay-btn-got-it" disabled style="width: 100%; height: 50px; margin-top: 6px; opacity: 0.35; cursor: not-allowed; pointer-events: none; filter: grayscale(0.5);">
          <span class="start-btn-text">GOT IT</span>
        </button>
      </div>
    </div>
  `;

  const link = buildRecoveryLink(recoveryCode);
  const copyBtn = layer.querySelector('#account-overlay-btn-copy');
  copyBtn?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(link);
      const span = copyBtn.querySelector('.start-btn-text') || copyBtn;
      span.textContent = 'COPIED!';
      setTimeout(() => { span.textContent = 'COPY ACCOUNT LINK'; }, 1500);
    } catch {
      const span = copyBtn.querySelector('.start-btn-text') || copyBtn;
      span.textContent = 'COPY FAILED - SELECT MANUALLY';
    }
  });

  const dismissBtn = layer.querySelector('#account-overlay-btn-got-it');
  const ackCheckbox = layer.querySelector('#account-ack-checkbox');

  ackCheckbox?.addEventListener('change', () => {
    const isChecked = !!ackCheckbox.checked;
    dismissBtn.disabled = !isChecked;
    if (isChecked) {
      dismissBtn.style.opacity = '1';
      dismissBtn.style.cursor = 'pointer';
      dismissBtn.style.pointerEvents = 'auto';
      dismissBtn.style.filter = 'none';
    } else {
      dismissBtn.style.opacity = '0.35';
      dismissBtn.style.cursor = 'not-allowed';
      dismissBtn.style.pointerEvents = 'none';
      dismissBtn.style.filter = 'grayscale(0.5)';
    }
  });

  dismissBtn?.addEventListener('click', () => {
    if (ackCheckbox?.checked) {
      layer.remove();
      activeRecoveryCodeOverlay = null;
    }
  });

  if (activeRecoveryCodeOverlay) {
    activeRecoveryCodeOverlay.remove();
    activeRecoveryCodeOverlay = null;
  }
  document.body.appendChild(layer);
  activeRecoveryCodeOverlay = layer;
}

let activeRecoverOverlay = null;
let activeRecoveryCodeOverlay = null;

export function closeRecoverPrompt() {
  if (activeRecoverOverlay) {
    activeRecoverOverlay.remove();
    activeRecoverOverlay = null;
  }
}

// Opens a standalone "enter your recovery code" prompt, independent of the
// creation overlay above - e.g. wired to a menu button later. On success,
// reloads the page so every module that already cached the old identity
// (WSMatchClient, HUD) starts clean against the recovered account.
export function showRecoverPrompt() {
  if (activeRecoverOverlay && document.body.contains(activeRecoverOverlay)) {
    const existingInput = activeRecoverOverlay.querySelector('#account-recover-input');
    existingInput?.focus();
    return activeRecoverOverlay;
  }
  if (activeRecoverOverlay) {
    activeRecoverOverlay.remove();
    activeRecoverOverlay = null;
  }

  const layer = el('div', 'fullscreen-dialog-overlay active account-overlay');
  layer.style.zIndex = '12000';

  layer.innerHTML = `
    <div class="fullscreen-dialog-bg-layer" aria-hidden="true">
      <div class="fullscreen-dialog-bg-image"></div>
      <div class="fullscreen-dialog-scrim"></div>
      <div class="fullscreen-dialog-vignette"></div>
    </div>
    <div class="fullscreen-dialog-container" style="justify-content: center; align-items: center; min-height: 100vh;">
      <div class="fullscreen-card-panel" style="max-width: 480px; width: 92%; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 16px; padding: clamp(28px, 4vh, 40px) clamp(22px, 3.5vw, 36px);">
        <div class="fullscreen-dialog-status-pill status-pill-circular" style="margin-bottom: -2px;">
          <span class="status-indicator-dot"></span>
        </div>
        <div class="account-overlay-title" style="font-family: 'Orbitron', sans-serif; font-size: clamp(20px, 2.5vw, 24px); font-weight: 900; letter-spacing: 2px; color: #00f0ff; text-shadow: 0 0 16px rgba(0, 240, 255, 0.45); margin: 0;">
          RECOVER ACCOUNT
        </div>
        <div class="account-overlay-text" style="font-family: 'Rajdhani', sans-serif; font-size: clamp(15px, 1.8vw, 17px); font-weight: 600; line-height: 1.5; color: #cbd5e1; margin: 0;">
          Enter your 12-character recovery code:
        </div>
        <input type="text" id="account-recover-input" class="account-overlay-input" placeholder="XXXX-XXXX-XXXX" maxlength="14" style="width: 100%; height: 50px; font-family: 'IBM Plex Mono', monospace; font-size: 18px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; text-align: center; border-radius: 8px; border: 1.5px solid rgba(0, 240, 255, 0.4); background: rgba(0, 240, 255, 0.08); color: #00f0ff; box-sizing: border-box; outline: none; box-shadow: 0 0 14px rgba(0, 240, 255, 0.2); transition: border-color 0.2s, box-shadow 0.2s;" />
        <div id="account-recover-status" class="account-overlay-status" style="min-height: 18px; font-family: 'Rajdhani', sans-serif; font-size: 14px; font-weight: 700; letter-spacing: 0.5px; margin: 0;"></div>
        <div style="display: flex; gap: 12px; width: 100%; margin-top: 4px;">
          <button type="button" class="start-btn start-btn-secondary" id="account-recover-cancel" style="flex: 1; height: 50px;">
            <span class="start-btn-text">CANCEL</span>
          </button>
          <button type="button" class="start-btn start-btn-primary" id="account-recover-submit" style="flex: 1; height: 50px;">
            <span class="start-btn-text">RECOVER</span>
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(layer);
  activeRecoverOverlay = layer;

  const input = layer.querySelector('#account-recover-input');
  const status = layer.querySelector('#account-recover-status');
  const cancelBtn = layer.querySelector('#account-recover-cancel');
  const confirmBtn = layer.querySelector('#account-recover-submit');

  const closePrompt = () => {
    layer.remove();
    if (activeRecoverOverlay === layer) {
      activeRecoverOverlay = null;
    }
    window.removeEventListener('keydown', handleKey, true);
  };

  cancelBtn?.addEventListener('click', closePrompt);

  layer.addEventListener('click', (e) => {
    if (e.target === layer || e.target.classList.contains('fullscreen-dialog-container')) {
      closePrompt();
    }
  });

  const handleKey = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closePrompt();
    }
  };
  window.addEventListener('keydown', handleKey, true);

  const handleRecover = async () => {
    const code = input.value.trim();
    if (!code) return;
    confirmBtn.disabled = true;
    status.textContent = 'Checking...';
    status.classList.remove('account-overlay-status-error');

    const result = await recoverAccount(code);
    if (result && result.accountId) {
      status.textContent = 'Account recovered - reloading...';
      setTimeout(() => location.reload(), 600);
    } else {
      status.textContent = result?.message || 'Invalid code, or too many attempts - try again later.';
      status.classList.add('account-overlay-status-error');
      confirmBtn.disabled = false;
    }
  };

  confirmBtn?.addEventListener('click', handleRecover);
  input?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') handleRecover();
  });
  input?.focus();

  return layer;
}

let activeSwitchOverlay = null;

export function closeAccountSwitchPrompt() {
  if (activeSwitchOverlay) {
    activeSwitchOverlay.remove();
    activeSwitchOverlay = null;
  }
}

// Confirmation gate shown when an incoming recovery link (#a=...) arrives on a
// device that already has an authenticated Commander session. Prevents silent
// session adoption, cross-account contamination, and accidental account overwrites.
export function showAccountSwitchPrompt({ currentAuth, incomingCode, incomingAccount, onCancel }) {
  closeAccountSwitchPrompt();

  const layer = el('div', 'fullscreen-dialog-overlay active account-switch-overlay');
  layer.style.zIndex = '13000';

  const currentDisplayName = currentAuth?.displayName || 'Commander';
  const currentAccountId = currentAuth?.accountId ? `#${currentAuth.accountId}` : 'GUEST';
  const incomingLabel = incomingAccount?.accountId
    ? `${escapeHtml(incomingAccount.displayName || 'Commander')} (#${incomingAccount.accountId})`
    : 'RECOVER ACCOUNT';

  layer.innerHTML = `
    <div class="fullscreen-dialog-bg-layer" aria-hidden="true">
      <div class="fullscreen-dialog-scrim"></div>
      <div class="fullscreen-dialog-vignette"></div>
    </div>
    <div class="fullscreen-dialog-container" style="justify-content: center; align-items: center; min-height: 100dvh; padding: clamp(8px, 2vh, 16px); box-sizing: border-box;">
      <div class="fullscreen-card-panel" style="max-width: 480px; width: 94%; max-height: 90dvh; overflow-y: auto; display: flex; flex-direction: column; align-items: center; text-align: center; gap: clamp(10px, 2vh, 16px); padding: clamp(14px, 3vh, 26px) clamp(16px, 3vw, 28px); border: 1.5px solid rgba(251, 191, 36, 0.45); box-shadow: 0 0 24px rgba(251, 191, 36, 0.25), inset 0 0 12px rgba(251, 191, 36, 0.08); box-sizing: border-box;">
        <div class="fullscreen-dialog-status-pill status-pill-circular status-gold" style="margin-bottom: -4px;">
          <span class="status-indicator-dot" style="background: #fbbf24; box-shadow: 0 0 10px #fbbf24;"></span>
        </div>

        <div style="display: flex; flex-direction: column; gap: 3px;">
          <div style="font-family: 'Rajdhani', sans-serif; font-size: 11px; font-weight: 800; letter-spacing: 2px; color: #fbbf24;">
            SECURITY GATE · SESSION CONFLICT
          </div>
          <div style="font-family: 'Orbitron', sans-serif; font-size: clamp(18px, 2.2vw, 22px); font-weight: 900; letter-spacing: 1.5px; color: #f8fafc; text-shadow: 0 0 16px rgba(251, 191, 36, 0.4);">
            SWITCH COMMANDER?
          </div>
        </div>

        <div style="font-family: 'Rajdhani', sans-serif; font-size: clamp(14px, 1.6vw, 15.5px); font-weight: 600; line-height: 1.4; color: #cbd5e1; margin: 0;">
          An active Commander account is already linked to this device. Loading this link will replace your local session.
        </div>

        <div style="width: 100%; background: rgba(0, 0, 0, 0.45); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 6px; padding: 8px 12px; display: flex; justify-content: space-between; align-items: center; box-sizing: border-box; font-family: 'IBM Plex Mono', monospace; font-size: 13px;">
          <div style="text-align: left;">
            <div style="font-size: 10px; font-family: 'Rajdhani', sans-serif; font-weight: 800; color: #94a3b8; letter-spacing: 1px;">ACTIVE ON DEVICE</div>
            <div style="color: #00f0ff; font-weight: 700;">${escapeHtml(currentDisplayName)} (${currentAccountId})</div>
          </div>
          <div style="color: #fbbf24; font-size: 16px; margin: 0 8px;">➔</div>
          <div style="text-align: right;">
            <div style="font-size: 10px; font-family: 'Rajdhani', sans-serif; font-weight: 800; color: #94a3b8; letter-spacing: 1px;">INCOMING LINK</div>
            <div style="color: #fbbf24; font-weight: 700;">${incomingLabel}</div>
          </div>
        </div>

        <div style="font-family: 'Rajdhani', sans-serif; font-size: 12.5px; font-weight: 700; color: #f87171; letter-spacing: 0.5px;">
          ⚠ Ensure you have saved your current recovery key before switching.
        </div>

        <div id="account-switch-status" class="account-overlay-status" style="min-height: 18px; font-family: 'Rajdhani', sans-serif; font-size: 14px; font-weight: 700; letter-spacing: 0.5px; margin: 0;"></div>

        <div style="display: flex; gap: 10px; width: 100%; margin-top: 4px;">
          <button type="button" class="start-btn start-btn-secondary" id="btn-cancel-account-switch" style="flex: 1; height: 44px;">
            <span class="start-btn-text">KEEP CURRENT</span>
          </button>
          <button type="button" class="start-btn start-btn-red" id="btn-confirm-account-switch" style="flex: 1; height: 44px;">
            <span class="start-btn-text">SWITCH ACCOUNT</span>
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(layer);
  activeSwitchOverlay = layer;

  const cancelBtn = layer.querySelector('#btn-cancel-account-switch');
  const confirmBtn = layer.querySelector('#btn-confirm-account-switch');
  const status = layer.querySelector('#account-switch-status');

  const closeDialog = () => {
    closeAccountSwitchPrompt();
    window.removeEventListener('keydown', handleKey, true);
  };

  const handleCancel = () => {
    closeDialog();
    if (typeof onCancel === 'function') onCancel();
  };

  const handleConfirm = async () => {
    confirmBtn.disabled = true;
    confirmBtn.style.opacity = '0.5';
    cancelBtn.disabled = true;
    status.textContent = 'Switching account...';
    status.classList.remove('account-overlay-status-error');

    const result = await recoverAccount(incomingCode);
    if (result && result.accountId) {
      status.textContent = 'Account restored - reloading...';
      setTimeout(() => location.reload(), 500);
    } else {
      status.textContent = result?.message || 'Invalid recovery link or network error.';
      status.classList.add('account-overlay-status-error');
      confirmBtn.disabled = false;
      confirmBtn.style.opacity = '1';
      cancelBtn.disabled = false;
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleCancel();
    }
  };
  window.addEventListener('keydown', handleKey, true);

  cancelBtn?.addEventListener('click', handleCancel);
  confirmBtn?.addEventListener('click', handleConfirm);

  layer.addEventListener('click', (e) => {
    if (e.target === layer || e.target.classList.contains('fullscreen-dialog-container')) {
      handleCancel();
    }
  });

  return layer;
}
